export type ActivityLogMentionType = 'employee' | 'agent';

export type ParsedActivityLogMention = {
  type: ActivityLogMentionType;
  label?: string;
  id?: string;
};

export type ActivityLogMentionMatch = {
  index: number;
  length: number;
  parsed: ParsedActivityLogMention;
};

const MENTION_TYPE_BY_VALUE = {
  employee: 'employee',
  agent: 'agent',
} as const satisfies Record<string, ActivityLogMentionType>;

const NEW_MENTION_TOKEN_PATTERN = /\{\{((?:\\.|[^}\\])+)\}\}/g;
const ESCAPED_TOKEN_VALUE_CHARS = new Set(['\\', ',', ':', '}']);

export const ACTIVITY_LOG_AGENT_FALLBACK_LABEL = 'Agent';

const isActivityLogMentionType = (value: string): value is ActivityLogMentionType =>
  Object.prototype.hasOwnProperty.call(MENTION_TYPE_BY_VALUE, value);

function escapeMentionTokenValue(value: string) {
  return value.replace(/\\/g, '\\\\').replace(/,/g, '\\,').replace(/:/g, '\\:').replace(/}/g, '\\}');
}

function parseMentionTokenFields(fields: string): ParsedActivityLogMention | null {
  const parsedFields = new Map<string, string>();
  let currentKey = '';
  let currentValue = '';
  let isReadingValue = false;
  let index = 0;

  const commitField = () => {
    if (currentKey) {
      parsedFields.set(currentKey, currentValue);
    }
    currentKey = '';
    currentValue = '';
    isReadingValue = false;
  };

  while (index < fields.length) {
    const char = fields[index];
    const next = fields[index + 1];
    const isEscapedValue = char === '\\' && next !== undefined && ESCAPED_TOKEN_VALUE_CHARS.has(next);

    if (isEscapedValue) {
      if (isReadingValue) {
        currentValue += next;
      } else {
        currentKey += next;
      }
      index += 2;
    } else if (!isReadingValue && char === ':') {
      isReadingValue = true;
      index += 1;
    } else if (isReadingValue && char === ',') {
      commitField();
      index += 1;
    } else {
      if (isReadingValue) {
        currentValue += char;
      } else {
        currentKey += char;
      }
      index += 1;
    }
  }

  commitField();

  const rawType = parsedFields.get('type');
  if (!rawType || !isActivityLogMentionType(rawType)) {
    return null;
  }

  if (rawType === 'employee' && !parsedFields.get('id')) {
    return null;
  }

  const parsed: ParsedActivityLogMention = {
    type: MENTION_TYPE_BY_VALUE[rawType],
  };

  if (parsedFields.has('label')) {
    parsed.label = parsedFields.get('label') ?? '';
  }

  if (parsedFields.has('id')) {
    parsed.id = parsedFields.get('id') ?? '';
  }

  return parsed;
}

export function findActivityLogMentionMatches(text: string): ActivityLogMentionMatch[] {
  const matches: ActivityLogMentionMatch[] = [];

  for (const match of text.matchAll(NEW_MENTION_TOKEN_PATTERN)) {
    const fields = match[1];
    const parsed = parseMentionTokenFields(fields);
    if (parsed) {
      matches.push({
        index: match.index ?? 0,
        length: match[0].length,
        parsed,
      });
    }
  }

  return matches;
}

export function getActivityLogMentionDisplayLabel(parsed: ParsedActivityLogMention, aiAgentLabel: string) {
  switch (parsed.type) {
    case 'agent':
      return aiAgentLabel || parsed.label || ACTIVITY_LOG_AGENT_FALLBACK_LABEL;
    case 'employee':
      return parsed.label || 'Employee';
    default: {
      const exhaustiveCheck: never = parsed.type;
      return exhaustiveCheck;
    }
  }
}

export function serializeActivityLogMentionToken(parsed: ParsedActivityLogMention) {
  const parts: string[] = [];

  if (parsed.label) {
    parts.push(`label:${escapeMentionTokenValue(parsed.label)}`);
  }

  if (parsed.type === 'employee' && parsed.id) {
    parts.push(`id:${escapeMentionTokenValue(parsed.id)}`);
  }

  parts.push(`type:${parsed.type}`);

  return `{{${parts.join(',')}}}`;
}
